// CRM-owned membership roster. Backed by /api/v1/crm/members — the org-level
// CRM access list. Replaces the prior wrapper around TG's /users/team-members.
//
// Shape is normalized to look like the old hook ({ _id, firstName, lastName,
// email, role, avatarUrl }) so consumers (Members list, project assignee
// pickers, Leads owner dropdown, etc.) didn't need rewriting. The `role`
// field now carries the CRM role (owner|manager|agent|viewer) instead of the
// TG role — intentional, since CRM access decisions should follow CRM roles.

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";

const LIST_KEY = ["crm", "members"];
const ELIGIBLE_KEY = ["crm", "members", "eligible"];

function normalizeMember(raw) {
  if (!raw) return null;
  const u = raw.user || {};
  const id = String(u._id || raw.userId);
  return {
    _id: id,
    membershipId: raw._id,
    userId: raw.userId,
    firstName: u.firstName || "",
    lastName: u.lastName || "",
    email: u.email || null,
    stubEmail: u.stubEmail || null,
    avatarUrl: u.avatar || null,
    role: raw.role,        // CRM role
    status: raw.status,
    addedAt: raw.addedAt,
  };
}

export function useTeamMembers() {
  const q = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const { data } = await crmApi.get("/members");
      const items = Array.isArray(data?.items) ? data.items : [];
      return items.map(normalizeMember).filter(Boolean);
    },
    staleTime: 5 * 60_000,
  });

  const byId = useMemo(() => {
    const m = new Map();
    (q.data || []).forEach((u) => m.set(String(u._id), u));
    return m;
  }, [q.data]);

  return { ...q, members: q.data || [], byId };
}

// The set of userIds assigned to a given CRM project. Backed by
// /projects/:projectId/members, which returns assignment rows ({ userId, ... })
// from crm_project_members — IDs only, no user detail. Used to scope owner
// pickers to a project's roster. Pass no projectId to get an empty set.
export function useProjectMemberIds(projectId) {
  const q = useQuery({
    queryKey: ["crm", "projects", projectId, "members"],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await crmApi.get(`/projects/${projectId}/members`);
      const items = Array.isArray(data?.members) ? data.members : [];
      return items.map((m) => String(m.userId)).filter(Boolean);
    },
    staleTime: 5 * 60_000,
  });
  const idSet = useMemo(() => new Set(q.data || []), [q.data]);
  return { ...q, ids: q.data || [], idSet };
}

// Org CRM roster narrowed to the currently-selected project's assigned members.
// Joins the full-detail roster (useTeamMembers) against the project's userId set
// (useProjectMemberIds) so owner pickers list names/avatars but only for users
// who actually belong to the active project. While the project membership is
// still loading we fall back to the full roster to avoid flashing an empty list.
// The current user is always included regardless of project membership rows —
// the project creator/owner may never appear in crm_project_members but should
// always be selectable as an owner.
export function useProjectScopedMembers() {
  const { projectId } = useSelectedProject();
  const roster = useTeamMembers();
  const { idSet, isLoading: idsLoading, isFetched: idsFetched } = useProjectMemberIds(projectId);
  const me = useMyCrmRole();
  const myUserId = me.data?.userId ? String(me.data.userId) : null;

  const scoped = useMemo(() => {
    // Never surface orphaned/incomplete memberships (no name + no email) in
    // owner assign/filter pickers — they render as "Unknown" and confuse users.
    const identifiable = roster.members.filter(isIdentifiableMember);
    if (!projectId || !idsFetched) return identifiable;
    return identifiable.filter((u) => {
      const id = String(u._id);
      return idSet.has(id) || (myUserId && id === myUserId);
    });
  }, [projectId, idsFetched, roster.members, idSet, myUserId]);

  const byId = useMemo(() => {
    const m = new Map();
    scoped.forEach((u) => m.set(String(u._id), u));
    return m;
  }, [scoped]);

  return {
    members: scoped,
    byId,
    isLoading: roster.isLoading || idsLoading,
  };
}

/** True when a roster row has a displayable identity (name or email). */
export function isIdentifiableMember(u) {
  if (!u) return false;
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return Boolean(name || u.email || u.stubEmail);
}

export function formatMember(u) {
  if (!u) return "—";
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.email || u.stubEmail || "Unknown";
}

// Eligible TG users in the org who are not yet CRM members — used by the
// "Add member" picker in Settings.
//
// Frontend-side filter (no backend change): only show users whose
// `permissions.crmAccess === true` OR who are org admins (admin role bypasses
// the per-user toggle since admins have implicit access to everything).
// This keeps the CRM gating decision visible: the admin must first toggle
// the CRM permission in Team Management before that user appears here.
export function useEligibleUsers() {
  return useQuery({
    queryKey: ELIGIBLE_KEY,
    queryFn: async () => {
      const { data } = await crmApi.get("/members/eligible");
      const items = Array.isArray(data?.items) ? data.items : [];
      return items.filter((u) => {
        const isAdmin = u.role === "org_admin" || u.role === "main_admin";
        if (isAdmin) return true;
        return u.permissions?.crmAccess === true;
      });
    },
    staleTime: 60_000,
  });
}

// Current user's CRM role. Drives admin-only UI (Settings tab visibility, owner
// actions in Members, etc.). Roles ranked: owner > manager > agent > viewer.
// `isAdmin` is true for owner+manager — they see and edit everything except
// owner-only escalations; agent/viewer get the limited Profile-style view.
export function useMyCrmRole({ enabled = true } = {}) {
  const q = useQuery({
    queryKey: ["crm", "members", "me"],
    queryFn: async () => {
      const { data } = await crmApi.get("/members/me");
      return data;
    },
    staleTime: 5 * 60_000,
    enabled,
  });
  const role = q.data?.role || null;
  return {
    ...q,
    role,
    companyIds: q.data?.companyIds,
    isOwner: role === "owner",
    isManager: role === "manager",
    isAgent: role === "agent",
    isViewer: role === "viewer",
    isAdmin: role === "owner" || role === "manager",
  };
}

// Member CUD also affects the current user's own role (if they edited
// themselves) and the leads list (owner-name display joins on members).
// /me drives Settings-tab visibility, owner-column gating in Leads/Pipeline,
// and admin-only buttons everywhere — so it MUST be invalidated.
function invalidateMembers(qc) {
  qc.invalidateQueries({ queryKey: LIST_KEY });
  qc.invalidateQueries({ queryKey: ELIGIBLE_KEY });
  qc.invalidateQueries({ queryKey: ["crm", "members", "me"] });
  qc.invalidateQueries({ queryKey: ["crm", "leads"] });
  qc.invalidateQueries({ queryKey: ["crm", "pipelines"] });
}

export function useAddCrmMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }) => {
      const { data } = await crmApi.post("/members", { userId, role });
      return data?.member;
    },
    onSuccess: () => invalidateMembers(qc),
  });
}

export function useUpdateCrmMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ membershipId, patch }) => {
      const { data } = await crmApi.patch(`/members/${membershipId}`, patch);
      return data?.member;
    },
    onSuccess: () => invalidateMembers(qc),
  });
}

export function useRemoveCrmMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membershipId) => {
      const { data } = await crmApi.delete(`/members/${membershipId}`);
      return data;
    },
    onSuccess: () => invalidateMembers(qc),
  });
}
